import {
    XUIEditableTable,
    XUIEditableTableBody,
    XUIEditableTableCellReadOnly,
    XUIEditableTableFoot,
    XUIEditableTableFootAction,
    XUIEditableTableFootActions,
    XUIEditableTableHead,
    XUIEditableTableHeadingCell,
    XUIEditableTableRow,
} from '@xero/xui/react/editabletable';
import { XUIPanelSection } from '@xero/xui/react/panel';
import { XUIPickitem } from '@xero/xui/react/picklist';
import XUIToast, { XUIToastWrapper } from '@xero/xui/react/toast';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ClearingAccountModal, IClearingAccount } from '../../../../components/ClearingAccountModal';
import DeleteModal from '../../../../components/DeleteModal';
import { DropDownSelect } from '../../../../components/DropDownSelect';
import { InfoPopover } from '../../../../components/InfoPopover';
import Link from '../../../../components/Link';
import TaxDisclaimer from '../../../../components/TaxDisclaimer';
import useDidMountEffect from '../../../../helpers/hooks';
import {
    IPaymentGateway,
    IPaymentGatewayDetail,
    IShopifySettingsResponse,
    ITablePaymentGatewayDetail,
} from '../../../../models';
import {
    clearingAccountSelectors,
    createClearingAccount,
    paymentGatewayChanged,
} from '../../../../redux/slices/AccountSetupSlice';
import { settingsSelectors } from '../../../../redux/slices/SettingsSlice';
import { deepClone, useAppDispatch } from '../../../../redux/store';
import { ShopifyLang } from '../../../../static/lang/en-NZ/shopify';
import { PaymentGatewayBankAccountDeletedState } from '../models';
import './PaymentGatewaysSetupTab.css';
import { BankAccountDropDown, ClearingAccountDropdown, GatewayDropdown } from './TableFunctions';
import { IDataRow } from './TableFunctions/models';
import { isClearingAccountMissing } from './TableFunctions/utils';

export interface IPaymentGatewaysSetupTabProps {
    integrationLang: ShopifyLang;
    settings?: IShopifySettingsResponse;
    showTaxDisclaimer: boolean;
    onGatewayChange: (paymentGatewayDetails: Record<string, IPaymentGatewayDetail>) => void;
    onValid: (isValid: boolean) => void;
    paymentGatewayBankAccountDeletedState: PaymentGatewayBankAccountDeletedState;
}

export const PaymentGatewaysSetupTab: FC<IPaymentGatewaysSetupTabProps> = ({
    integrationLang,
    showTaxDisclaimer,
    onGatewayChange,
    onValid,
    paymentGatewayBankAccountDeletedState,
}) => {
    const dispatch = useAppDispatch();

    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const reduxGatewayDetails = useSelector(clearingAccountSelectors.paymentGatewayDetails);
    const isSyncingSettings = useSelector(settingsSelectors.isSyncing);
    const isCreatingClearingAccount = useSelector(clearingAccountSelectors.isCreating);
    const clearingAccountError = useSelector(clearingAccountSelectors.error);

    const lang = integrationLang.SETUP_PAGE.STORE;

    const [showClearingAccountModal, setShowClearingAccountModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<IDataRow>();
    const [createClearingAccountGateway, setCreateClearingAccountGateway] = useState('');
    const [currentTableData, setCurrentTableData] = useState<ITablePaymentGatewayDetail[]>([]);
    const [invalidRows, setInvalidRows] = useState<string[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [selectPGCellInvalid, setSelectPGCellInvalid] = useState(false);
    const [isAddGatewayButtonDisabled, setIsAddGatewayButtonDisabled] = useState(false);
    const [deletedPaymentGateways, setDeletedPaymentGateways] = useState<string[]>([]);

    useEffect(() => {
        onValid(!selectPGCellInvalid && !(invalidRows && invalidRows.length > 0));
    }, [selectPGCellInvalid, invalidRows, onValid]);

    useEffect(() => {
        const paymentGatewayDetails = settings.paymentGatewayDetails ? deepClone(settings.paymentGatewayDetails) : {};
        Object.entries(paymentGatewayDetails).forEach(([key, details]) => {
            const rowDetails = { paymentType: key, ...details };
            if (!deletedPaymentGateways.includes(key)) {
                const index = currentTableData.findIndex((item: { paymentType: string }) => item.paymentType === key);
                if (index === -1) {
                    currentTableData.push(rowDetails);
                } else {
                    currentTableData[index] = rowDetails;
                }

                const clearingAccount = settings?.bankAccounts.find(
                    (f) => f.accountId === rowDetails.clearingAccountId ?? ''
                );
                const bankAccount = settings?.bankAccounts.find((f) => f.accountId === rowDetails.bankAccountId ?? '');
                if (
                    isClearingAccountMissing(clearingAccount, bankAccount) &&
                    invalidRows.indexOf(rowDetails.paymentType) === -1
                ) {
                    invalidRows.push(rowDetails.paymentType);
                }
                if (key === lang.ROW_SELECT_PAYMENT_TYPE && (details.clearingAccountId !== null && details.clearingAccountId !== undefined)) {
                    setSelectPGCellInvalid(true);
                }
            }
        });

        setCurrentTableData([...currentTableData]);
        setInvalidRows([...invalidRows]);
    // All dependencies used in a hook should be injected here.
    // This is to ensure that the application reacts to all state and state does not get out of sync.
    // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.paymentGatewayDetails]);

    useEffect(() => {
        if (isCreatingClearingAccount) {
            onGatewayChange(reduxGatewayDetails);
            if (createClearingAccountGateway === lang.ROW_SELECT_PAYMENT_TYPE) {
                setSelectPGCellInvalid(true);
            }
        }
    // All dependencies used in a hook should be injected here.
    // This is to ensure that the application reacts to all state and state does not get out of sync.
    // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxGatewayDetails, isCreatingClearingAccount]);

    useDidMountEffect(() => {
        if (!isCreatingClearingAccount && !clearingAccountError) {
            setShowClearingAccountModal(false);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 5000);

            // make sure the new clearing account row is not marked as invalid
            const invalidRowIndex = invalidRows.findIndex((invalidRow) => invalidRow === createClearingAccountGateway);
            if (invalidRowIndex !== -1) {
                invalidRows.splice(invalidRowIndex, 1);
                setInvalidRows([...invalidRows]);
            }
        }
    }, [isCreatingClearingAccount, isSyncingSettings, clearingAccountError]);

    const handleRemove = () => {
        if (rowToDelete) {
            const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
            if (paymentGatewayDetails) {

                setDeletedPaymentGateways([...deletedPaymentGateways, rowToDelete.paymentType]);
                delete paymentGatewayDetails[rowToDelete.paymentType];

                const invalidRowIndex = invalidRows.findIndex((invalidRow) => invalidRow === rowToDelete.paymentType);
                if (invalidRowIndex !== -1) {
                    invalidRows.splice(invalidRowIndex, 1);
                    setInvalidRows([...invalidRows]);
                }

                const index = currentTableData.findIndex(
                    (item: { paymentType: string }) => item.paymentType === rowToDelete.paymentType
                );
                if (index !== -1) {
                    currentTableData.splice(index, 1);
                    setCurrentTableData([...currentTableData]);
                }
                if (settings?.paymentGateways.some((pg) => pg.code === rowToDelete.paymentType)) {
                    setIsAddGatewayButtonDisabled(false);
                }

                if (rowToDelete.paymentType === lang.ROW_SELECT_PAYMENT_TYPE) {
                    setSelectPGCellInvalid(false);
                }
                onGatewayChange(paymentGatewayDetails);
            }
        }
        setShowDeleteModal(false);
    };

    const handleAddRow = () => {
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails ?? {});
        const index = currentTableData.findIndex(
            (item: { paymentType: string }) => item.paymentType === lang.ROW_SELECT_PAYMENT_TYPE
        );
        if (index === -1) {
            setCurrentTableData([
                ...currentTableData,
                {
                    paymentType: lang.ROW_SELECT_PAYMENT_TYPE,
                    createInvoice: true,
                },
            ]);
            paymentGatewayDetails[lang.ROW_SELECT_PAYMENT_TYPE] = {
                createInvoice: true,
            };
            onGatewayChange(paymentGatewayDetails);
        }
    };

    const handleCreateInvoice = (row: IDataRow, active: boolean) => () => {
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
        if (paymentGatewayDetails) {
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === row.paymentType
            );
            if (active) {
                paymentGatewayDetails[row.paymentType].createInvoice = active;
                currentTableData[index].createInvoice = active;
            } else {
                paymentGatewayDetails[row.paymentType] = { createInvoice: active };
                currentTableData[index] = { paymentType: row.paymentType, createInvoice: active };
            }
            setCurrentTableData(currentTableData);
            onGatewayChange(paymentGatewayDetails);
            dispatch(
                paymentGatewayChanged(row.paymentType, {
                    ...paymentGatewayDetails[row.paymentType],
                    createInvoice: active,
                })
            );
        }
    };

    const handleClearingAccountCreated = async (newClearingAccount: IClearingAccount): Promise<void> => {
        if (newClearingAccount.bankAccountName) {
            dispatch(createClearingAccount(newClearingAccount, createClearingAccountGateway));
        }
    };

    const handleCreateNewClearing = (paymentGateway: string) => {
        setCreateClearingAccountGateway(paymentGateway);
        setShowClearingAccountModal(true);
    };

    const handlePaymentGatewayUpdate = (paymentGatewayDetails: Record<string, IPaymentGatewayDetail>, paymentGatewayCode: string) => {
        //if a user adds back a gateway that they previously deleted, remove that gateway from the deletedPaymentGateways array
        if (paymentGatewayDetails) {
            setDeletedPaymentGateways(deletedPaymentGateways.filter((deletedPaymentGateway) => deletedPaymentGateway !== paymentGatewayCode));
            onGatewayChange(paymentGatewayDetails);
        }
    };

    const getDefaultClearingAccountName = (): string => {
        return settings
            ? settings.recommendedClearingAccountName
            : integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.DEFAULT_BANK_ACCOUNT_NAME;
    };

    const onTableRowRemove = (row: IDataRow) => {
        setRowToDelete(row);
        setShowDeleteModal(true);
    };

    const isExistingPaymentGateway = (row: IDataRow) => {
        if (!settings || !settings.paymentGatewayDetails) {
            return false;
        }

        return row.paymentType in settings.paymentGatewayDetails && row.paymentType !== lang.ROW_SELECT_PAYMENT_TYPE;
    };

    const createRow = (row: IDataRow, index: number): ReactNode => {
        let rowClass = 'payment-type-row';
        if (!row.createInvoices) {
            rowClass += ' disable-account';
        }
        return (
            <XUIEditableTableRow key={index} onRemove={() => onTableRowRemove(row)} className={rowClass}>
                {isExistingPaymentGateway(row) ? (
                    <XUIEditableTableCellReadOnly>
                        <div className='read-only-payment-gateway'>
                            {settings?.paymentGateways?.find((f) => f.code === row.paymentType)?.name ??
                                row.paymentType}
                        </div>
                    </XUIEditableTableCellReadOnly>
                ) : (
                    <GatewayDropdown
                        onGatewayChange={handlePaymentGatewayUpdate}
                        deletedGateways={deletedPaymentGateways}
                        dataRow={row}
                        tableDataState={[currentTableData, setCurrentTableData]}
                        setIsAddGatewayButtonDisabled={setIsAddGatewayButtonDisabled}
                        setSelectPGCellInvalid={setSelectPGCellInvalid}
                        lang={lang}
                    />
                )}
                <XUIEditableTableCellReadOnly>
                    <DropDownSelect
                        buttonContent={row.createInvoices ? 'Yes' : 'No'}
                        className='create-invoice-dropdown'
                    >
                        <XUIPickitem
                            id='create-invoice-dropdown-0'
                            onClick={handleCreateInvoice(row, true)}
                            isSelected={row.createInvoices}
                        >
                            {lang.CREATE_INVOICE}
                        </XUIPickitem>
                        <XUIPickitem
                            id='create-invoice-dropdown-1'
                            onClick={handleCreateInvoice(row, false)}
                            isSelected={!row.createInvoices}
                        >
                            {lang.DON_T_CREATE_INVOICE}
                        </XUIPickitem>
                    </DropDownSelect>
                </XUIEditableTableCellReadOnly>
                <XUIEditableTableCellReadOnly>
                    <ClearingAccountDropdown
                        currentTableData={currentTableData}
                        setCurrentTableData={setCurrentTableData}
                        dataRow={row}
                        onGatewayChange={onGatewayChange}
                        onClearingAccountCreate={handleCreateNewClearing}
                        lang={lang}
                        invalidRowsState={[invalidRows, setInvalidRows]}
                        setSelectPGCellInvalid={setSelectPGCellInvalid}
                    />
                </XUIEditableTableCellReadOnly>
                <XUIEditableTableCellReadOnly>
                    <BankAccountDropDown
                        currentTableData={currentTableData}
                        setCurrentTableData={setCurrentTableData}
                        dataRow={row}
                        onGatewayChange={onGatewayChange}
                        reconcilable={row.reconcilable}
                        lang={lang}
                        invalidRowsState={[invalidRows, setInvalidRows]}
                        paymentGatewayBankAccountDeletedState={paymentGatewayBankAccountDeletedState}
                    />
                </XUIEditableTableCellReadOnly>
            </XUIEditableTableRow>
        );
    };

    // temporary solution
    const checkReconcilable = (paymentGateways: IPaymentGateway[] | null, paymentType: string) => {
        if (!paymentGateways || paymentGateways.length === 0) {
            return ['shopify_payments', 'paypal'].indexOf(paymentType) > -1;
        } else {
            return paymentGateways?.find((f) => f.code === paymentType)?.reconcilable ?? false;
        }
    };

    const rowData = currentTableData
        ? Object.entries(currentTableData).map(
              ([, value]): IDataRow => ({
                  paymentType: value.paymentType,
                  createInvoices: value.createInvoice ?? true,
                  clearingAccount: settings?.bankAccounts.find((f) => f.accountId === value.clearingAccountId ?? ''),
                  bankAccount: value.bankAccountId
                      ? settings?.bankAccounts.find((f) => f.accountId === value.bankAccountId)
                      : undefined,
                  reconcilable: checkReconcilable(settings?.paymentGateways!, value.paymentType),
              })
          )
        : [];

    const accountCreatedToast = showToast ? (
        <XUIToastWrapper>
            <XUIToast className='xui-toast-positive' message={lang.CLEARING_ACCOUNT_CREATED_TOAST} />
        </XUIToastWrapper>
    ) : null;

    return (
        <>
            <XUIPanelSection className='shopify-panel--section' heading={lang.TITLE}>
                {showTaxDisclaimer ? (
                    <div className='content shopify-content xui-padding-top-3xlarge'>
                        <TaxDisclaimer blurb={lang.TAX_DISCLAIMER} showIcon={true} />
                    </div>
                ) : null}
                <div className='content shopify-content xui-padding-bottom-3xlarge'>
                    <div className='shopify-panel--description xui-padding-top-xlarge'>{lang.DESCRIPTION}</div>
                </div>
                <div className='table-content'>
                    <XUIEditableTable
                        rowOptions={{ isRemovable: true, removeButtonAriaLabel: lang.ROW_BUTTON_REMOVE_ARIA, removeColumnHeaderLabel: "[Placeholder] Remove column header" }}
                        ariaLabel='Remove row'
                    >
                        <XUIEditableTableHead>
                            <XUIEditableTableRow>
                                <XUIEditableTableHeadingCell>
                                    {lang.ROW_HEADER_PAYMENT_GATEWAY}{' '}
                                </XUIEditableTableHeadingCell>
                                <XUIEditableTableHeadingCell>
                                    {lang.ROW_HEADER_CREATE_INVOICE}{' '}
                                    <InfoPopover>{lang.ROW_HEADER_CREATE_INVOICE_POPOVER}</InfoPopover>
                                </XUIEditableTableHeadingCell>
                                <XUIEditableTableHeadingCell>
                                    {lang.ROW_HEADER_CLEARING_ACCOUNT}{' '}
                                    <span className='optional-heading'>{lang.ROW_HEADER_OPTIONAL}</span>{' '}
                                    <InfoPopover>{lang.ROW_HEADER_CLEARING_ACCOUNT_POPOVER}</InfoPopover>
                                </XUIEditableTableHeadingCell>
                                <XUIEditableTableHeadingCell>
                                    {lang.ROW_HEADER_BANK_ACCOUNT}{' '}
                                    <span className='optional-heading'>{lang.ROW_HEADER_OPTIONAL}</span>{' '}
                                    <InfoPopover>{lang.ROW_HEADER_BANK_ACCOUNT_POPOVER}</InfoPopover>
                                </XUIEditableTableHeadingCell>
                            </XUIEditableTableRow>
                        </XUIEditableTableHead>
                        <XUIEditableTableBody>{rowData.map(createRow)}</XUIEditableTableBody>
                        <XUIEditableTableFoot className='table-footer'>
                            <XUIEditableTableFootActions>
                                <XUIEditableTableFootAction
                                    buttonProps={{ isDisabled: isAddGatewayButtonDisabled }}
                                    buttonContent={lang.ADD_PAYMENT}
                                    onClick={handleAddRow}
                                />
                            </XUIEditableTableFootActions>
                        </XUIEditableTableFoot>
                    </XUIEditableTable>
                </div>
                <div className='xui-padding-top-xlarge xui-text-align-center xui-stepper-link-description xui-padding-bottom-3xlarge'>
                    {integrationLang.SETUP_PAGE.ADVISOR_PREAMBLE}
                    <Link
                        displayLinkIcon={true}
                        href={integrationLang.SETUP_PAGE.ADVISOR_LINK}
                    >
                        {integrationLang.SETUP_PAGE.ADVISOR_TEXT}
                    </Link>
                </div>
            </XUIPanelSection>
            <ClearingAccountModal
                headerText={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.MODAL_HEADER}
                messageText={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.MODAL_MESSAGE}
                accountNameLabel={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.ACCOUNT_NAME_LABEL}
                accountNameHint={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.ACCOUNT_NAME_HINT}
                defaultBankAccountName={getDefaultClearingAccountName()}
                cancelButtonText={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.CANCEL_BUTTON}
                createButtonText={integrationLang.SETUP_PAGE.CLEARING_ACCOUNT.CREATE_BUTTON}
                isModalActive={showClearingAccountModal}
                onPrimaryClicked={handleClearingAccountCreated}
                onSecondaryClicked={() => setShowClearingAccountModal(false)}
            />
            <DeleteModal
                title={integrationLang.SETUP_PAGE.DELETE_PAYMENT_GATEWAY.MODAL_HEADER}
                cancelButtonText={integrationLang.SETUP_PAGE.DELETE_PAYMENT_GATEWAY.CANCEL_BUTTON}
                deleteButtonText={integrationLang.SETUP_PAGE.DELETE_PAYMENT_GATEWAY.DELETE_BUTTON}
                showModal={showDeleteModal}
                onPrimaryClicked={() => handleRemove()}
                onSecondaryClicked={() => setShowDeleteModal(false)}
                qaHook='delete-modal'
            >
                {integrationLang.SETUP_PAGE.DELETE_PAYMENT_GATEWAY.MODAL_MESSAGE}
            </DeleteModal>
            {accountCreatedToast}
        </>
    );
};
